import React, { useMemo } from "react";
import ReactDOM from "react-dom/client";
import mixpanel from "mixpanel-browser";
import { OfficeRouter } from "./routes/OfficeRouter.jsx";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./hooks/useAuth";
import { MixPanelIdentitySync } from "./hooks/MixPanelIdentitySync";
import { ActivityFeedContextProvider } from "./hooks/ActivityFeedContext";
import { CenturionContextProvider } from "./hooks/CenturionContext";
import { PartnersContextProvider } from "./hooks/PartnersContext";
import { MemberContextProvider } from "./hooks/MemberContext";
import { ActionHubContextProvider } from "./contexts/ActionHubContext";
import { ActionableItemsProvider } from "./hooks/ActionableItemsContext";
import { DistributorContextProvider } from "./hooks/DistributorContext";
import { LanguageContextProvider } from "./hooks/LanguageContext";
import { withLDProvider } from "launchdarkly-react-client-sdk";
import "./index.css";
import { LicenseInfo } from "@mui/x-data-grid-pro";
import { LanguageDirectionWrapper } from "./hooks/LanguageDirectionWrapper";
import { SnackbarProvider } from './contexts/SnackbarContext';
import VersionChangeBanner from "./components/VersionChangeBanner";
import UnicityGoBanner from "./components/UnicityGoBanner";
import './services/translation/i18n';
import { initializeDatadog } from './services/datadog';
import { datadogRum } from '@datadog/browser-rum';
import ErrorBoundary from './components/ErrorBoundary';
import { ThemeProvider } from "./hooks/ThemeContext";
import { CssBaseline } from "@mui/material";
import { TranslationProvider, i18n } from "@unicity/design-system";
import { localTranslationTable} from "./hooks/localTranslationTable";
import { DevOpsLDProvider } from "./contexts/DevOpsLDContext";

// Pre-register English translations before first render so useTranslation().ready
// is true on mount and T/useTranslateKey components show values, not raw keys.
// The CDN backend fetch still runs async and merges updated strings on top.
["en", "en-US"].forEach((lang) =>
  i18n.addResourceBundle(lang, "translation", localTranslationTable, true, false)
);

// Webpack HMR module declaration
declare const module: {
  hot?: {
    accept(): void;
  };
} & NodeModule;

// Initialize Datadog
initializeDatadog();

// Initialize Mixpanel once at app bootstrap. Previously this lived at module
// scope inside `useMixPanel`, which is fine functionally (modules are only
// evaluated once) but couples init to the first import of that hook and is
// easy to accidentally double-init from a parallel file. Anchoring it here
// keeps initialization in one obvious place.
// Office Prod (3139795) and Office QA (3144308) are both EU-residency
// projects on eu.mixpanel.com. Mixpanel retires its legacy US→EU forwarding
// on 2026-07-01; after that, events sent to the default US host are dropped.
// Hardcoding the EU host unconditionally — no env var — since both envs are EU.
mixpanel.init(process.env.REACT_APP_MIXPANEL_TOKEN ?? "", {
  api_host: "https://api-eu.mixpanel.com",
  debug: !process.env.REACT_APP_IS_PRODUCTION,
  track_pageview: false,
  persistence: "localStorage",
});

// GO-3121: one-time cleanup of the legacy persistent API-response cache.
// useApi previously stored GET responses in localStorage under the
// "office-web-cache:" prefix with no eviction; on long-lived sessions this
// filled the ~5MB quota and made unrelated localStorage writes throw
// QuotaExceededError (crashing the Library page). The cache now lives in
// memory (see src/hooks/useApi.js); purge any leftover entries so already-
// affected users recover without manually clearing site data. Iterate
// backwards because removeItem reindexes the store.
try {
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key && key.startsWith("office-web-cache:")) {
      localStorage.removeItem(key);
    }
  }
} catch {
  // localStorage unavailable (private mode, etc.) — non-fatal.
}

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_PRO_LICENSE_KEY ?? "");


function OfficeApp() {
  const translationConfig = useMemo(() => {
    return {
      fallbackLanguage: "en-US", 
      translationUrl: process.env.REACT_APP_TRANSLATION_URL ?? 'https://cdn.unicity.com/translations/qa/newOffice/',
      localTranslationUrl: localTranslationTable as Record<string, string>,
    };
  }, []);

  const AppWrapper = React.Fragment;

  return (
    <AppWrapper>
      <DevOpsLDProvider>
      <AuthProvider>
        <ThemeProvider>
          <ErrorBoundary>
            <SnackbarProvider>
              <TranslationProvider config={translationConfig}>
                <LanguageContextProvider>
                  <MixPanelIdentitySync />
                  <LanguageDirectionWrapper>
                      <MemberContextProvider>
                        <DistributorContextProvider>
                          <PartnersContextProvider>
                            <ActionHubContextProvider>
                              <ActionableItemsProvider>
                                <CenturionContextProvider>
                                  <ActivityFeedContextProvider>
                                    <HelmetProvider>
                                      <CssBaseline />
                                      <VersionChangeBanner />
                                      <UnicityGoBanner />
                                      <OfficeRouter />
                                    </HelmetProvider>
                                  </ActivityFeedContextProvider>
                                </CenturionContextProvider>
                              </ActionableItemsProvider>
                            </ActionHubContextProvider>
                          </PartnersContextProvider>
                        </DistributorContextProvider>
                      </MemberContextProvider>
                  </LanguageDirectionWrapper>
                </LanguageContextProvider>
              </TranslationProvider>
            </SnackbarProvider>
          </ErrorBoundary>
        </ThemeProvider>
      </AuthProvider>
      </DevOpsLDProvider>
    </AppWrapper>
  );
}

const LDProvider = withLDProvider({
  clientSideID: process.env.REACT_APP_LAUNCHDARKLY_KEY ?? "",
  options: {
    inspectors: [
      {
        type: "flag-used",
        name: "dd-inspector",
        method: (key, detail) => {
          datadogRum.addFeatureFlagEvaluation(key, detail.value);
        },
      },
    ],
  },
})(OfficeApp);

if (module.hot) {
  module.hot.accept();
}

const root = ReactDOM.createRoot(document.getElementById("root")!);
root.render(<LDProvider />);
